/**
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 **/
import React, { ReactElement, useMemo, useCallback, useState, useRef, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { gql, useQuery } from '@apollo/client';
import Button from '@userzoomlibraries/toolkit-button';
import QrCode from '@userzoomlibraries/toolkit-qr-code';
import Tooltip from '@userzoomlibraries/toolkit-tooltip';
import copyToClipboard from '@userzoomlibraries/toolkit-js-utils/copy-to-clipboard';
import { VARIANTS } from '@userzoomlibraries/toolkit-themes-userzoom-platform-button';
import { Devices } from '@userzoomplatform/ua-parser';
import { useDevice } from '@userzoomplatform/pe-study-layout';
import { DeviceRestrictions } from '../../types/restrictions';
import Layout from '../../components/layout';
import PageCard from '../../components/page-card';
import RestrictionCard from '../../components/restriction-card';
import { Namespaces } from '../../utils/i18n';

const { Desktop, Mobile, Tablet } = Devices;

const studyRestrictionsQuery = gql`
	query restrictions {
		restrictions {
			deviceRestrictions {
				device
				os {
					name
					minVersion
					excludedVersions
				}
				browsers {
					name
					minVersion
					excludedVersions
				}
			}
			invitationLink
		}
	}
`;

interface RestrictionsPayload {
	restrictions: {
		deviceRestrictions: DeviceRestrictions[];
		invitationLink: string;
	};
}

export type Props = {
	className?: string;
	// device: Device;
};

const defaultProps = {
	className: '',
};

const StudyRestrictions = ({ className }: Props): ReactElement => {
	const history = useHistory();
	const { i18n, t } = useTranslation(Namespaces.Common);
	const [tooltipOpened, setTooltipOpened] = useState(false);
	const studyUrlTextRef = useRef<HTMLParagraphElement>(null);
	const { device } = useDevice();

	const { error: studyRestrictionsError, data: studyRestrictionsData } = useQuery<RestrictionsPayload>(studyRestrictionsQuery);

	useEffect(() => {
		if (studyRestrictionsError) {
			history.replace('/forbidden');
		}
	}, [studyRestrictionsError, history]);

	const { deviceRestrictions = [], invitationLink = '' } = { ...studyRestrictionsData?.restrictions };

	const desktopOnly = useMemo(() => {
		const allowedDevices = deviceRestrictions
			.filter(({ device: allowedDevice, browsers, os }) => {
				if (allowedDevice === Desktop) {
					return browsers.length;
				}
				return os.length;
			})
			.map(({ device: allowedDevice }) => allowedDevice);
		return ![Mobile, Tablet].some((mobileDevice) => allowedDevices.includes(mobileDevice));
	}, [deviceRestrictions]);

	const onButtonClick = useCallback(() => {
		copyToClipboard(studyUrlTextRef?.current?.textContent, () => {
			setTooltipOpened(true);
			setTimeout(() => {
				setTooltipOpened(false);
			}, 1000);
		});
	}, []);

	return studyRestrictionsData ? (
		<div className={`study-restrictions ${className} is-${device?.type}`} dir={i18n.dir()}>
			<Layout>
				<PageCard>
					<h1 className="title">{t('restrictions.title')}</h1>
					<div className="devices">
						{deviceRestrictions.map(({ device: allowedDevice, browsers, os }) => {
							if (browsers.length || os.length) {
								return (
									<div key={`column-${allowedDevice}`} className="device">
										<RestrictionCard
											image={{ url: `${process.env.PUBLIC_URL}/images/${allowedDevice}.svg`, alt: allowedDevice }}
											device={allowedDevice}
											browsers={browsers}
											os={os}
										/>
									</div>
								);
							}
							return null;
						})}
					</div>
					<div className="entrance">
						{!desktopOnly && (
							<div className="study-qr" data-or={t('connectors.or')}>
								<h3>{t('restrictions.studyQR.title')}</h3>
								<QrCode value={invitationLink} size={100} />
								<p>{t('restrictions.studyQR.instructions')}</p>
							</div>
						)}
						<div className={`study-link ${desktopOnly ? 'desktop-only' : ''}`}>
							<div className="text">
								<h3>{t('restrictions.studyLink.title')}</h3>
								<p ref={studyUrlTextRef}>{invitationLink}</p>
							</div>
							<Tooltip trigger="manual" opened={tooltipOpened} content={t('feedback.copied')} placement="top">
								<Button id="button-3" onClick={onButtonClick} variant={VARIANTS.SECONDARY} rounded>
									{t('restrictions.studyLink.cta')}
								</Button>
							</Tooltip>
						</div>
					</div>
				</PageCard>
			</Layout>
		</div>
	) : (
		<div />
	);
};

StudyRestrictions.defaultProps = defaultProps;
export default StudyRestrictions;
